import React, { useEffect, useState, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { essentialLocales, setAppLocales, i18n } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { AppLayout, colors } from '@fs/zion-ui'
import { HelperMode } from '@fs/zion-helper-mode'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { getPreference, setPreference } from '@fs/zion-preferences'
import { NoticeLoading } from '@fs/zion-icon'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import utils from './lib/utils'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: [
        /-extension:\//, // Browser extensions (chrome-extension, moz-extension, etc.)
        /Extension context invalidated/, // Chrome Extension error that is not caught by the first
        /QuotaExceededError/, // Non-critical localstorage errors
        /StorageQuotaExceeded/, // Non-critical localstorage errors
        'Request failed with status code 401', // Happens when a user session times out
      ],
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// Add any app logic here as flags, languages and translations are now configured and available
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)

  const [pageBanner, setPageBanner] = useState('')
  const [open, setOpen] = useState(false)
  const [isStatsPage, setIsStatsPage] = useState(false)

  let alertMessage = ''
  if (pageBanner === 'following') {
    alertMessage = i18n.t(
      'alert-banner.following-labels',
      'You can add labels to the people you are following. For more information, click Learn More.'
    )
  } else if (pageBanner === 'contributions') {
    alertMessage = i18n.t(
      'alert-banner.my-contributions',
      'Your contributions to Family Tree are now listed under this new menu called My Contributions. For more information, click Learn More.'
    )
  }
  useEffect(() => {
    if (pageBanner) {
      getPreference(utils.PREFERENCE_KEYS[`${pageBanner}.alert`])
        .then((pref) => {
          setOpen(!!pageBanner && !pref)
        })
        .catch(console.error)
    }
  }, [pageBanner])
  return (
    <Router basename={base}>
      <Root
        analytics
        footer={{ hidden: !isStatsPage }}
        alertBannerProps={{
          type: 'info',
          message: alertMessage,
          confirmText: i18n.t('common-ui:learn-more.action', 'Learn More'),
          dismissText: i18n.t('dismiss.button', 'Dismiss'),
          onConfirm: () => {
            const learnMoreUrl = utils.getTranslatedUrl(pageBanner === 'following')
            window.open(learnMoreUrl, '_blank')
          },
          onDismiss: (closeBanner) => {
            setPreference(utils.PREFERENCE_KEYS[`${pageBanner}.alert`], true)
            closeBanner()
          },
          open,
          setOpen,
        }}
      >
        <AppLayout fullWidth backgroundColor={colors.gray02}>
          <HelperMode />
          <App setIsStatsPage={setIsStatsPage} setPageBanner={setPageBanner} />
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
